import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Paper, Typography, Box, Button, TextField, Grid, Chip, Alert,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  FormControlLabel, Switch, Autocomplete, MenuItem, IconButton, TablePagination, Stack
} from '@mui/material';
import { Add, Edit, Delete, Inventory2, UploadFile } from '@mui/icons-material';
import api from '../services/api';
import PriceListImportDialog from '../components/merchant/PriceListImportDialog';

const CATEGORY_OPTIONS = [
  'Cement & Concrete', 'Bricks & Blocks', 'Steel & Rebar', 'Roofing Materials',
  'Timber', 'Plumbing', 'Electrical', 'Paint & Finishes'
];
const UNIT_OPTIONS = ['bag', 'tonne', 'kg', 'm³', 'm²', 'metre', 'litre', 'each', 'load', 'sheet', 'roll'];

const PAGE_SIZE = 50;

const daysAgo = (iso) => Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);

const emptyForm = {
  sku: '', name: '', category: '', description: '', unit: 'each', unitPrice: '',
  inStock: true, availableQuantity: '', isPublished: true
};

const MerchantProductsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // item being edited, or null = create
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null); // item pending delete confirm
  const [importOpen, setImportOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [meta, setMeta] = useState({ staleCount: 0, totalAll: 0, staleDays: 14 });
  const [staleOnly, setStaleOnly] = useState(false);
  const [query, setQuery] = useState('');
  const [notice, setNotice] = useState('');

  // Search runs on the server: an imported catalog can hold thousands of products.
  useEffect(() => {
    const t = setTimeout(() => { setQuery(search.trim()); setPage(0); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const loadItems = useCallback(async () => {
    try {
      setError('');
      const res = await api.get('/merchants/catalog', {
        params: { limit: PAGE_SIZE, offset: page * PAGE_SIZE, search: query || undefined, stale: staleOnly || undefined },
      });
      if (res.data.success) {
        setItems(res.data.data || []);
        setTotal(res.data.pagination?.total ?? 0);
        if (res.data.meta) setMeta(res.data.meta);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Please complete your merchant profile first.');
      } else {
        console.error('Error loading catalog:', err);
        setError('Failed to load your products.');
      }
    } finally {
      setLoading(false);
    }
  }, [page, query, staleOnly]);

  useEffect(() => { loadItems(); }, [loadItems]);

  const confirmPrices = async () => {
    try {
      const res = await api.post('/merchants/catalog/confirm-prices');
      setNotice(`${res.data.data.confirmed} prices confirmed as current.`);
      setStaleOnly(false);
      await loadItems();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to confirm prices.');
    }
  };

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (item) => {
    setEditing(item);
    setForm({
      sku: item.sku || '', name: item.name, category: item.category || '', description: item.description || '',
      unit: item.unit, unitPrice: item.unit_price, inStock: item.in_stock,
      availableQuantity: item.available_quantity ?? '', isPublished: item.is_published
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      const payload = {
        sku: form.sku.trim() || null,
        name: form.name.trim(),
        category: form.category || null,
        description: form.description || null,
        unit: form.unit,
        unitPrice: parseFloat(form.unitPrice),
        inStock: form.inStock,
        availableQuantity: form.availableQuantity === '' ? null : parseFloat(form.availableQuantity),
        isPublished: form.isPublished
      };
      const res = editing
        ? await api.put(`/merchants/catalog/${editing.id}`, payload)
        : await api.post('/merchants/catalog', payload);
      if (res.data.success) {
        setDialogOpen(false);
        await loadItems();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save product.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/merchants/catalog/${deleting.id}`);
      setDeleting(null);
      await loadItems();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete product.');
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>Products</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<UploadFile />} onClick={() => setImportOpen(true)}>Import price list</Button>
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}>Add Product</Button>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}
      {meta.staleCount > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}
          action={(
            <Stack direction="row" spacing={1}>
              <Button color="inherit" size="small" onClick={() => { setStaleOnly((v) => !v); setPage(0); }}>
                {staleOnly ? 'Show all' : 'Show them'}
              </Button>
              <Button color="inherit" size="small" onClick={() => setImportOpen(true)}>Import new list</Button>
              <Button color="inherit" size="small" onClick={confirmPrices}>Prices are still right</Button>
            </Stack>
          )}>
          {meta.staleCount} of {meta.totalAll} prices have not been confirmed in {meta.staleDays} days, so buyers can’t see
          those products. Import your latest price list, or confirm the prices are still right.
        </Alert>
      )}

      {meta.totalAll === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Inventory2 sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>Build your product catalog</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your catalog speeds up quotations (one-tap price fill) and lets buyers
            order published items directly for pickup. Upload the price list from your
            stock system, or add products one at a time.
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="center">
            <Button variant="contained" startIcon={<UploadFile />} onClick={() => setImportOpen(true)}>Import price list</Button>
            <Button variant="outlined" startIcon={<Add />} onClick={openCreate}>Add a product</Button>
          </Stack>
        </Paper>
      ) : (
        <Paper>
          <Box sx={{ p: 2 }}>
            <TextField size="small" placeholder="Search name or code…" value={search}
              onChange={(e) => setSearch(e.target.value)} sx={{ width: 280 }} />
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell align="right">Price (USD)</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell sx={{ color: 'text.secondary' }}>{item.sku || '—'}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{item.name}</TableCell>
                    <TableCell>{item.category || '—'}</TableCell>
                    <TableCell>{item.unit}</TableCell>
                    <TableCell align="right">${parseFloat(item.unit_price).toFixed(2)}</TableCell>
                    <TableCell align="right">{item.available_quantity ?? '—'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Chip size="small" label={item.in_stock ? 'In stock' : 'Out of stock'}
                          color={item.in_stock ? 'success' : 'default'} />
                        {!item.is_published && <Chip size="small" label="Unpublished" variant="outlined" />}
                        {item.price_stale && (
                          <Chip size="small" color="warning" variant="outlined"
                            label={`Price ${daysAgo(item.price_confirmed_at)} days old`} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openEdit(item)}><Edit fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => setDeleting(item)}><Delete fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={total} page={page} rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]} onPageChange={(e, p) => setPage(p)} />
        </Paper>
      )}

      <PriceListImportDialog open={importOpen} endpoint="/merchants/catalog/import"
        onClose={() => setImportOpen(false)} onApplied={loadItems} />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Product' : 'Add Product'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Product code" value={form.sku}
                onChange={(e) => setForm({ ...form, sku: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField fullWidth label="Product Name *" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete freeSolo options={CATEGORY_OPTIONS} value={form.category}
                onInputChange={(e, v) => setForm({ ...form, category: v })}
                renderInput={(params) => <TextField {...params} label="Category" />} />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField select fullWidth label="Unit *" value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}>
                {UNIT_OPTIONS.map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth type="number" label="Price (USD) *" value={form.unitPrice}
                onChange={(e) => setForm({ ...form, unitPrice: e.target.value })}
                inputProps={{ min: 0, step: 0.01 }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} label="Description" value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </Grid>
            <Grid item xs={6} md={4}>
              <TextField fullWidth type="number" label="Available Quantity" value={form.availableQuantity}
                onChange={(e) => setForm({ ...form, availableQuantity: e.target.value })}
                helperText="Leave blank if not tracked" inputProps={{ min: 0 }} />
            </Grid>
            <Grid item xs={6} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel label="In stock" control={
                <Switch checked={form.inStock} onChange={(e) => setForm({ ...form, inStock: e.target.checked })} />} />
            </Grid>
            <Grid item xs={6} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel label="Published" control={
                <Switch checked={form.isPublished} onChange={(e) => setForm({ ...form, isPublished: e.target.checked })} />} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={saving || !form.name.trim() || form.unitPrice === ''}
            onClick={handleSave}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleting} onClose={() => setDeleting(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete product?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            "{deleting?.name}" will be removed from your catalog. Past orders are unaffected.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MerchantProductsPage;
